import { Button, Icon, useThemeMode } from "@meiden-connect/ui";
import { FeedbackIconData } from "@meiden-connect/material-symbols";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { DASHBOARD_THEME_MODE_ATTRIBUTE } from "../../components/dashboard-shell/dashboard-theme-mode-attribute";
import { useAuthSession } from "../../auth/auth-session-context";
import { getWebConfig } from "../../env";
import { isEmbedMode } from "../../lib/embed-mode";
import { createFeedbackAccessChannel } from "./access-channel";
import { createFeedbackHostBridge } from "./host-bridge";
import { hasCompatibleFeedback } from "./capabilities";
import { feedbackPageContext } from "./page-context";
import { readFeedbackAccessToken } from "./feedback-access-token";
import { CONNECT_FEEDBACK_CLIENT_ID, type HostState } from "./embed-protocol";
import { RESIZABLE_MIN_VIEWPORT_WIDTH_PX, useMiniWindowLayout } from "./use-mini-window-layout";

/** 利用者が変更したパネルサイズの保存キー。 */
const SIZE_STORAGE_KEY = "refinement-canvas.feedback.size.v1";
/** 再試行を数える時間窓（ms）。 */
const RETRY_WINDOW_MS = 300_000;
/** 時間窓内に許す再試行の回数。 */
const RETRY_LIMIT = 3;
/** 再試行どうしの最小間隔（ms）。 */
const RETRY_INTERVAL_MS = 60_000;
/** 子へ伝える基準フォントサイズの下限・上限（px。契約の検証範囲と合わせる）。 */
const BASE_FONT_MIN_PX = 8;
const BASE_FONT_MAX_PX = 48;
/** リサイズハンドルの向き。 */
const RESIZE_DIRECTIONS = ["width", "height", "both"] as const;

/**
 * 右下 FAB から Connect Feedback の投稿フォームをミニウィンドウで開く入口。
 *
 * 次をすべて満たすときだけ描画する。満たさない場合は iframe も FAB も作らない。
 * - `connectFeedbackOrigin` が設定されている
 * - 参照専用の埋め込みモード（`?embed=1`）ではない（親フレーム側の FAB を使う）
 * - ログイン済みで、セッション復元中でもない
 * - 認証コールバック系の画面ではない
 */
export function ConnectFeedbackWindow(): ReactNode {
  const { user, isRestoring } = useAuthSession();
  const origin = getWebConfig().connectFeedbackOrigin;
  // 認証コールバック系の判定は入口 URL（フルロード）だけで起きるため、マウント時の値で足りる。
  // 画面遷移によるページ情報の変化は、`get-context` の応答時に読み直す。
  const path = window.location.pathname;
  if (
    origin === undefined ||
    isEmbedMode() ||
    user === undefined ||
    isRestoring ||
    path.startsWith("/auth/") ||
    path === "/idp-cb"
  ) {
    return undefined;
  }
  // 利用者が変わったら iframe を作り直し、入力内容と資格情報を引き継がない。
  return <FeedbackWindow key={user.userId} origin={origin} userId={user.userId} />;
}

function FeedbackWindow({
  origin,
  userId,
}: {
  readonly origin: string;
  readonly userId: string;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [contentHeight, setContentHeight] = useState<number | undefined>(undefined);
  const layout = useMiniWindowLayout(SIZE_STORAGE_KEY, { contentHeight });
  const theme = useThemeMode();
  const [instanceId] = useState(() => crypto.randomUUID());
  const [started, setStarted] = useState(false);
  const [documentAttempt, setDocumentAttempt] = useState(0);
  const [documentMounted, setDocumentMounted] = useState(true);
  const succeeded = useRef(false);
  const [available, setAvailable] = useState(false);
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(false);
  const [checking, setChecking] = useState(false);
  const frame = useRef<HTMLIFrameElement>(null);
  const panelElement = useRef<HTMLElement>(null);
  const fab = useRef<HTMLDivElement>(null);
  const generation = useRef(0);
  const alive = useRef(true);
  // 非同期コールバックから最新の表示状態を読むための鏡。state の更新待ちに依存させない。
  const current = useRef({ open, theme });
  const bridge = useRef<ReturnType<typeof createFeedbackHostBridge> | undefined>(undefined);
  const retryTimes = useRef<number[]>([]);

  const hostState = useCallback(
    (): HostState => ({
      visible: current.current.open,
      theme: current.current.theme,
      // 本プロジェクトは子からの親画面遷移を提供しないため、世代だけで整合を取る。
      revision: 0,
      openGeneration: generation.current,
      baseFontPx: Math.min(
        BASE_FONT_MAX_PX,
        Math.max(
          BASE_FONT_MIN_PX,
          parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
        )
      ),
    }),
    []
  );

  useLayoutEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      bridge.current?.dispose();
    };
  }, []);

  useLayoutEffect(() => {
    current.current = { open, theme };
    bridge.current?.update();
  }, [open, theme]);

  const minimize = useCallback(() => {
    if (!current.current.open) return;
    // 親の入力操作を妨げないよう、自分の窓にあったフォーカスだけを FAB へ戻す。
    const restoreFocus = panelElement.current?.contains(document.activeElement) === true;
    current.current.open = false;
    setOpen(false);
    bridge.current?.update();
    if (restoreFocus) fab.current?.querySelector("button")?.focus();
  }, []);

  // 互換性情報の照合。成功したときだけ iframe を作る。
  useEffect(() => {
    const controller = new AbortController();
    void hasCompatibleFeedback(
      origin,
      CONNECT_FEEDBACK_CLIENT_ID,
      window.location.origin,
      controller.signal
    ).then((ok) => {
      if (!controller.signal.aborted) setStarted(ok);
    });
    return () => controller.abort();
  }, [origin]);

  useLayoutEffect(() => {
    if (!started || !documentMounted) return undefined;
    let access: ReturnType<typeof createFeedbackAccessChannel> | undefined;
    const connection = createFeedbackHostBridge({
      origin,
      hostOrigin: window.location.origin,
      instanceId,
      userId,
      frame: () => frame.current?.contentWindow,
      state: hostState,
      context: () => feedbackPageContext(window.location.origin, window.location.pathname),
      onAvailable: (ok) => {
        if (!alive.current) return;
        setAvailable(ok);
        if (ok) succeeded.current = true;
        if (!ok) {
          // 親の初期期限を過ぎた子が周期認証を続けないよう、未成功文書だけ破棄する。
          if (!succeeded.current) setDocumentMounted(false);
          current.current.open = false;
          setOpen(false);
        }
      },
      onDocument: () => {
        access?.reset();
        // 文書が入れ替わったら、旧文書が申告した内容高さを引きずらない。
        if (alive.current) setContentHeight(undefined);
      },
      minimize,
      activity: (active, unseen) => {
        setSending(active);
        setUnread(!current.current.open && unseen);
      },
      contentSize: (height) => {
        if (alive.current) setContentHeight(height);
      },
    });
    bridge.current = connection;
    access = createFeedbackAccessChannel({
      origin,
      instanceId,
      frame: () => frame.current?.contentWindow,
      acceptsDocument: connection.acceptsDocument,
      read: () => readFeedbackAccessToken(origin, userId),
    });
    return () => {
      access?.dispose();
      connection.dispose();
      bridge.current = undefined;
    };
  }, [documentAttempt, documentMounted, hostState, instanceId, minimize, origin, started, userId]);

  // 一時的な失敗（配備前・オフライン）からの復帰。頻度を絞って再照合・再検証する。
  useEffect(() => {
    const controller = new AbortController();
    const retry = (): void => {
      if (available) return;
      const connection = bridge.current;
      if (started && !succeeded.current && documentMounted && connection?.needsRestart() !== true) {
        return;
      }
      const now = Date.now();
      const times = retryTimes.current.filter((time) => now - time < RETRY_WINDOW_MS);
      if (
        times.length >= RETRY_LIMIT ||
        (times.length > 0 && now - (times.at(-1) ?? 0) < RETRY_INTERVAL_MS)
      ) {
        return;
      }
      retryTimes.current = [...times, now];
      if (started) {
        // 初期化済みフォームは保持し、初回接続に失敗した文書だけ作り直す。
        if (succeeded.current) void connection?.verify(generation.current);
        else {
          setDocumentAttempt((attempt) => attempt + 1);
          setDocumentMounted(true);
        }
      } else {
        void hasCompatibleFeedback(
          origin,
          CONNECT_FEEDBACK_CLIENT_ID,
          window.location.origin,
          controller.signal
        ).then((ok) => {
          if (!controller.signal.aborted) setStarted(ok);
        });
      }
    };
    window.addEventListener("focus", retry);
    window.addEventListener("online", retry);
    return () => {
      controller.abort();
      window.removeEventListener("focus", retry);
      window.removeEventListener("online", retry);
    };
  }, [available, documentMounted, origin, started]);

  const toggle = useCallback(
    async (event: ReactMouseEvent) => {
      if (open) {
        minimize();
        return;
      }
      if (checking) return;
      // クリック由来でない（キーボード操作の）呼び出しでは、子の最初の入力へフォーカスを移す。
      const keyboard = event.detail === 0;
      setChecking(true);
      const next = generation.current + 1;
      const ok = await bridge.current?.verify(next);
      if (!alive.current) return;
      setChecking(false);
      if (ok !== true) return;
      generation.current = next;
      current.current.open = true;
      setOpen(true);
      setUnread(false);
      bridge.current?.update();
      if (keyboard) queueMicrotask(() => bridge.current?.focus());
    },
    [open, minimize, checking]
  );

  const click = useCallback(
    (event: ReactMouseEvent) => {
      void toggle(event);
    },
    [toggle]
  );

  const keyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (event.key === "Escape" && open) {
        // キャンバス側の Escape（モード解除）と二重に反応させない。
        event.stopPropagation();
        minimize();
      }
    },
    [open, minimize]
  );

  useEffect(() => {
    if (!open) layout.setResizing(false);
  }, [open, layout.setResizing]);

  const icon = <Icon d={FeedbackIconData} />;

  return createPortal(
    <div
      className="feedback-window-root"
      data-connect-feedback-window=""
      // document.body へ portal する UI の規約どおり、テーマ属性を根要素へ付けて
      // ダークの配色トークン（styles.css の [data-dashboard-theme-mode="dark"]）を解決させる。
      {...{ [DASHBOARD_THEME_MODE_ATTRIBUTE]: theme }}
      onKeyDown={keyDown}
    >
      <section
        ref={panelElement}
        id="connect-feedback-panel"
        className="feedback-window-panel"
        aria-label="Connect Feedback"
        hidden={!open || !available}
        inert={!open}
        style={layout.style}
      >
        <header className="feedback-window-titlebar">
          {icon}
          <strong>Connect Feedback</strong>
          {sending && <span className="feedback-window-stream" aria-label="処理中" />}
          <div className="feedback-window-actions">
            <Button
              variant="action"
              aria-label="サイズを初期化"
              title="サイズを初期化"
              onClick={layout.reset}
            >
              ↺
            </Button>
            <Button
              variant="action"
              aria-label={layout.maximized ? "標準サイズに戻す" : "フィードバックを拡大"}
              onClick={layout.enlarge}
            >
              {layout.maximized ? "▣" : "□"}
            </Button>
            <Button variant="action" aria-label="フィードバックを最小化" onClick={minimize}>
              −
            </Button>
          </div>
        </header>
        {started && documentMounted && (
          <iframe
            key={documentAttempt}
            ref={frame}
            className="feedback-window-frame"
            title="Connect Feedback 投稿"
            src={`${origin}/embed/${CONNECT_FEEDBACK_CLIENT_ID}?hostOrigin=${encodeURIComponent(window.location.origin)}#${instanceId}`}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
            referrerPolicy="no-referrer"
            style={layout.frameStyle}
          />
        )}
        {layout.viewport.width >= RESIZABLE_MIN_VIEWPORT_WIDTH_PX &&
          !layout.maximized &&
          RESIZE_DIRECTIONS.map((direction) => (
            <ResizeHandle
              key={direction}
              direction={direction}
              onStart={layout.startResize}
              onMove={layout.resize}
              onEnd={layout.endResize}
            />
          ))}
      </section>
      {/* ドラッグ中は iframe がポインタイベントを奪うため、透明の覆いで受け止める。 */}
      {layout.resizing && <div className="feedback-window-resize-shield" />}
      {available && (
        <div className="feedback-window-fab" ref={fab}>
          {/* 開閉状態は aria-expanded で伝える（開示ボタンの標準的な作法）。
              ラベルを切り替えるとタイトルバーの最小化ボタンと名前が重複するため固定する。 */}
          <Button
            variant="action"
            aria-label="フィードバックを送る"
            aria-expanded={open}
            aria-controls="connect-feedback-panel"
            disabled={checking}
            onClick={click}
          >
            {icon}
          </Button>
          {unread && (
            <span className="feedback-window-unread" aria-label="投稿結果があります" />
          )}
        </div>
      )}
    </div>,
    document.body
  );
}

function ResizeHandle({
  direction,
  onStart,
  onMove,
  onEnd,
}: {
  readonly direction: string;
  readonly onStart: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  readonly onMove: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  readonly onEnd: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-hidden="true"
      className={`feedback-window-resize feedback-window-resize--${direction}`}
      data-direction={direction}
      onPointerDown={onStart}
      onPointerMove={onMove}
      onPointerUp={onEnd}
      onPointerCancel={onEnd}
      onLostPointerCapture={onEnd}
    />
  );
}
